import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Box, Button, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  getColumnsWidthStyles,
  getSharedFormStyles,
} from 'app/pages/loanApplication/LoanApplication.sharedStyles';
import LoanTabLayout from 'app/pages/loanApplication/LoanTabLayout';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import { compose } from 'redux';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useLoanId from 'hooks/useLoanId';

import {
  generateNewUwReport,
  getLoanApplicationCommonDataByApplicationIdAction,
  getLoanApplicationHeaderViewAction,
  getUwReportsByLoanIdAction,
} from 'store/loanApplications/actions';
import { selectIsLoanCanceled, selectUwReportsList } from 'store/loanApplications/selectors';

import { UnderwritingTestIds } from '../../Underwriting.testIds';

import UWReportItem from './UWReportItem';

const UWReports = ({ setIsLoading, isLoading }) => {
  const loanId = useLoanId();
  const dispatch = useDispatch();

  const { applicationId } = useParams();

  const theme = useTheme();
  const classes = useMemo(() => getSharedFormStyles(theme), [theme]);
  const columnsStyles = useMemo(() => getColumnsWidthStyles(theme, { columns: 4 }), [theme]);

  const reports = useSelector(selectUwReportsList);
  const isLoanCanceled = useSelector(selectIsLoanCanceled);

  const sortedReports = useMemo(() => _.orderBy(reports, 'createdDate', 'desc'), [reports]);

  useEffectSafeStrictMode(() => {
    if (!loanId) {
      return;
    }

    setIsLoading(true);
    dispatch(getUwReportsByLoanIdAction({ loanId })).finally(() => setIsLoading(false));
  }, [dispatch, setIsLoading, loanId]);

  const onReportGenerate = useCallback(() => {
    setIsLoading(true);
    dispatch(generateNewUwReport({ loanId }))
      .then(() => dispatch(getLoanApplicationCommonDataByApplicationIdAction({ applicationId })))
      .then(() => dispatch(getLoanApplicationHeaderViewAction({ applicationId })))
      .finally(() => setIsLoading(false));
  }, [setIsLoading, dispatch, loanId, applicationId]);

  return (
    <div className="mt-32">
      <Typography className="mb-20" variant="bold16">
        UW Reports
      </Typography>

      <LoanTabLayout
        isLightBackgroundLayout
        actionItem={
          !isLoanCanceled && (
            <Button
              data-testid={UnderwritingTestIds.UW_REPORTS_GENERATE_NEW_REPORT_BUTTON}
              disabled={isLoading}
              sx={classes.addNewButton}
              type="button"
              variant="contained"
              onClick={onReportGenerate}
            >
              Generate New Report
            </Button>
          )
        }
        content={sortedReports?.map(report => (
          <UWReportItem key={report.reportId} report={report} />
        ))}
        top={
          <Box className="w-full flex" sx={columnsStyles.wrapper}>
            <Typography variant="medium16">Date</Typography>
            <Typography variant="medium16">Informational</Typography>
            <Typography variant="medium16">Warning</Typography>
            <Typography variant="medium16">Severe</Typography>
          </Box>
        }
      />
    </div>
  );
};

UWReports.propTypes = {
  ...WithLoadingPropTypes,
};

export default compose(withLoading, memo)(UWReports);
